import { addMonths, format, startOfMonth } from 'date-fns';
import { SELF_ID } from '../data/selfService';
import type { Employee, PtoRequest } from '../types/hr';
import { TODAY } from '../utils/format';
import { holidayApplies } from '../utils/holidays';
import { balanceFor, usedYtd } from '../utils/timeOff';
import { useHRStore } from './useHRStore';

const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');

function sumDays(list: PtoRequest[]): number {
  return list.reduce((s, r) => s + r.days, 0);
}

/** Everything the signed-in employee is allowed to see about themselves. */
export function useSelfService() {
  const state = useHRStore();
  const me = (state.employees.find((e) => e.id === SELF_ID) ?? state.employees[0]) as Employee;
  const policy = state.policies.find((p) => p.id === me.policyId);
  const manager = state.employees.find((e) => e.id === me.managerId);
  const balance = balanceFor(me.id)?.available ?? 0;

  const requests = state.requests.filter((r) => r.employeeId === me.id).sort((a, b) => b.start.localeCompare(a.start));
  const pendingDays = sumDays(requests.filter((r) => r.status === 'pending'));
  const scheduledDays = sumDays(requests.filter((r) => r.status === 'approved' && r.start > TODAY_ISO));
  const available = Math.max(0, balance - pendingDays - scheduledDays);
  const upcoming = requests
    .filter((r) => (r.status === 'approved' || r.status === 'pending') && r.end >= TODAY_ISO)
    .sort((a, b) => a.start.localeCompare(b.start));

  const holidays = state.holidays.filter((h) => holidayApplies(h, me.policyId, me.location));
  const nextAccrual = startOfMonth(addMonths(TODAY, 1));

  return {
    me,
    policy,
    manager,
    balance,
    available,
    pendingDays,
    scheduledDays,
    usedThisYear: usedYtd(me),
    requests,
    upcoming,
    holidays,
    nextAccrual,
    changeRequests: state.changeRequests.filter((c) => c.employeeId === me.id),
    docs: state.selfDocs,
    employees: state.employees,
    allRequests: state.requests,
  };
}
